import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiArrowLeft, FiGrid, FiList, FiPlusCircle, FiCheckSquare,
  FiShield, FiBarChart2, FiLogOut,
} from 'react-icons/fi';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../Styles/Expense.css';

import {
  getToken, setToken, clearToken, getCurrentUser,
  isTokenExpired, isExpenseApprover, isExpenseAdmin,
  getUserDisplayName,
} from '../../utils/expenseApi';

import ExpenseLogin      from './ExpenseLogin';
import ExpenseDashboard  from './ExpenseDashboard';
import MyClaims          from './MyClaims';
import SubmitClaim       from './SubmitClaim';
import ExpenseApprovals  from './ExpenseApprovals';
import PolicyRules       from './PolicyRules';
import ExpenseReports    from './ExpenseReports';
import ExpenseQuery      from './ExpenseQuery';

const pageVariants = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] } },
  exit:    { opacity: 0, y: -10, transition: { duration: 0.16, ease: 'easeIn' } },
};

export default function ExpenseApp() {
  const navigate = useNavigate();
  const [authed,    setAuthed]    = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [user,      setUser]      = useState(null);

  useEffect(() => {
    // Azure AD callback: backend redirects to /expense?token=<jwt>
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    if (urlToken) {
      setToken(urlToken);
      window.history.replaceState({}, '', window.location.pathname);
    }

    const stored = getToken();
    if (stored && !isTokenExpired()) {
      setAuthed(true);
      setUser(getCurrentUser());
    } else {
      clearToken();
    }
  }, []);

  // Listen for 401 forced logout from API interceptor
  useEffect(() => {
    const handleLogout = () => { setAuthed(false); setUser(null); };
    window.addEventListener('epe:logout', handleLogout);
    return () => window.removeEventListener('epe:logout', handleLogout);
  }, []);

  const handleLogout = () => {
    clearToken();
    setAuthed(false);
    setUser(null);
  };

  // Navigate to claims tab and optionally highlight a claim
  const handleSubmitSuccess = (_claimId) => {
    setActiveTab('claims');
  };

  if (!authed) return <ExpenseLogin />;

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: <FiGrid size={14} />,        show: true },
    { id: 'claims',    label: 'My Claims', icon: <FiList size={14} />,        show: true },
    { id: 'submit',    label: 'New Claim', icon: <FiPlusCircle size={14} />,  show: true },
    { id: 'approvals', label: 'Approvals', icon: <FiCheckSquare size={14} />, show: isExpenseApprover() },
    { id: 'policy',    label: 'Policy',    icon: <FiShield size={14} />,      show: true },
    { id: 'reports',   label: 'Reports',   icon: <FiBarChart2 size={14} />,   show: isExpenseApprover() || isExpenseAdmin() },
  ].filter(t => t.show);

  const renderTab = () => {
    switch (activeTab) {
      case 'dashboard': return <ExpenseDashboard onNavigate={setActiveTab} />;
      case 'claims':    return <MyClaims onNavigate={setActiveTab} />;
      case 'submit':    return <SubmitClaim onSuccess={handleSubmitSuccess} />;
      case 'approvals': return isExpenseApprover() ? <ExpenseApprovals /> : null;
      case 'policy':    return <PolicyRules />;
      case 'reports':   return (isExpenseApprover() || isExpenseAdmin()) ? <ExpenseReports /> : null;
      default:          return <ExpenseDashboard onNavigate={setActiveTab} />;
    }
  };

  const displayName = getUserDisplayName() || user?.sub || 'User';
  const roleName    = (user?.roles?.[0] || user?.role || '').replace(/_/g, ' ');

  return (
    <motion.div
      className="exp-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
    >
      {/* ── Header ── */}
      <motion.div
        className="exp-header"
        initial={{ y: -56, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="exp-header-left">
          <motion.button
            className="exp-back-btn"
            onClick={() => navigate('/home')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.95 }}
          >
            <FiArrowLeft size={14} />
            <span>Back to Home</span>
          </motion.button>
          <motion.div
            className="exp-header-title"
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.12, duration: 0.4 }}
          >
            <h1>Expense Management</h1>
            <p>Submit · Policy Check · Approve · Post to SAP</p>
          </motion.div>
        </div>

        <div className="exp-header-right">
          {user && (
            <div style={{ fontSize: '0.82rem', color: 'var(--e-muted)', textAlign: 'right' }}>
              <div style={{ fontWeight: 700, color: 'var(--e-txt)' }}>{displayName}</div>
              {roleName && <div style={{ fontSize: '0.74rem', textTransform: 'capitalize' }}>{roleName}</div>}
            </div>
          )}
          <motion.button
            className="exp-btn primary small"
            onClick={() => setActiveTab('submit')}
            whileTap={{ scale: 0.96 }}
          >
            <FiPlusCircle size={13} /> New Claim
          </motion.button>
          <motion.button
            className="exp-btn ghost"
            onClick={handleLogout}
            whileTap={{ scale: 0.96 }}
            title="Sign out"
          >
            <FiLogOut size={14} />
          </motion.button>
        </div>
      </motion.div>

      {/* ── Tab bar ── */}
      <motion.div
        className="exp-tab-bar"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.18, duration: 0.3 }}
      >
        {tabs.map((tab, i) => (
          <motion.button
            key={tab.id}
            className={`exp-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22 + i * 0.05 }}
            whileTap={{ scale: 0.96 }}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </motion.button>
        ))}
      </motion.div>

      {/* ── Content ── */}
      <div className="exp-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {renderTab()}
          </motion.div>
        </AnimatePresence>
      </div>

      <ExpenseQuery />

      <ToastContainer
        position="bottom-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        theme="light"
        toastStyle={{ fontFamily: "'Manrope',sans-serif", fontSize: '0.87rem' }}
      />
    </motion.div>
  );
}
